import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { IOnuDetails } from '@modules/clients/services/olt/models/onus.model';
import {
  IOnuHistoryItem,
  IOnuPortLink,
  IOnuView,
  IOnuViewEthernetPort,
  IOnuViewWifi,
  IOnuViewWifiPort,
  OnuAction,
  OnuReadResource,
} from '@modules/clients/services/olt/models/onu-view.model';
import { OltService } from '@modules/clients/services/olt/olt.service';
import { OnuViewService } from '@modules/clients/services/olt/onu-view.service';
import {
  ONU_EDIT_TITLES,
  OnuEditForm,
  OnuEditFormComponent,
} from '@sharedComponents/smart-olt-onu/onu-edit-form/onu-edit-form.component';
import { OnuGraphsComponent } from '@sharedComponents/smart-olt-onu/onu-graphs/onu-graphs.component';
import { OnuSyncFormComponent } from '@sharedComponents/smart-olt-onu/onu-sync-form/onu-sync-form.component';
import { OnuNotesComponent } from '@sharedComponents/smart-olt-onu/onu-notes/onu-notes.component';
import { OnuTr069Component } from '@sharedComponents/smart-olt-onu/onu-tr069/onu-tr069.component';
import { OnuSplitterDialogComponent } from '@sharedComponents/smart-olt-onu/onu-splitter/onu-splitter-dialog.component';
import {
  ONU_DIALOG_BREAKPOINTS,
  ONU_DIALOG_HEIGHT,
  ONU_DIALOG_WIDTH,
} from '@sharedComponents/smart-olt-onu/onu-dialog-size';
import { OnuStatusPanelComponent } from '@sharedComponents/smart-olt-onu/onu-status-panel/onu-status-panel.component';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService, SharedModule } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TabViewModule } from 'primeng/tabview';

type DiagKind = 'status' | 'running_config' | 'sw_info';

/** Acciones sobre el equipo, con el texto de confirmación del original. */
const DEVICE_ACTIONS: Record<
  'reboot' | 'resync' | 'restore_defaults' | 'enable' | 'disable' | 'firmware_upgrade',
  { label: string; message: string; severity: 'warning' | 'danger' | 'success' }
> = {
  reboot: { label: 'Reiniciar', message: '¿Reiniciar esta ONU?', severity: 'warning' },
  resync: {
    label: 'Resincronizar config',
    message:
      'Se volverá a aplicar en la OLT toda la configuración de SmartOLT para esta ONU. ¿Continuar?',
    severity: 'warning',
  },
  restore_defaults: {
    label: 'Restaurar de fábrica',
    message:
      'Se borrarán los ajustes hechos en el equipo (WiFi, port forwards, etc.) y luego se aplicará la configuración de SmartOLT. ¿Restaurar a valores de fábrica?',
    severity: 'danger',
  },
  enable: {
    label: 'Habilitar ONU',
    message: '¿Habilitar los servicios de esta ONU?',
    severity: 'success',
  },
  disable: {
    label: 'Deshabilitar ONU',
    message: 'La ONU quedará sin servicio (admin-disabled). ¿Deshabilitarla?',
    severity: 'danger',
  },
  firmware_upgrade: {
    label: 'Actualizar firmware',
    message:
      'Después de actualizar el firmware la ONU arrancará con valores de fábrica: se pierden LAN, SSID y contraseña WiFi (se conserva lo configurado en SmartOLT). Puede seguir el avance con "SW info". ¿Actualizar ahora?',
    severity: 'danger',
  },
};

const DIAG_TITLES: Record<DiagKind, string> = {
  status: 'Get status',
  running_config: 'Running config',
  sw_info: 'SW info',
};

/**
 * Detalle de la ONU con todo lo que permite la pantalla onu/view de SmartOLT:
 * datos y su edición, servicio, diagnóstico, gráficos, historial, notas y acciones.
 */
const DANGER_BUTTON = 'p-button-danger';

/** Lo común de las confirmaciones del modal: aviso y botón "No" sin relleno. */
const CONFIRM_DEFAULTS = {
  icon: 'pi pi-exclamation-triangle',
  rejectLabel: 'No',
  rejectButtonStyleClass: 'p-button-text',
};

@Component({
  selector: 'app-datails-smart-olt-configurations',
  standalone: true,
  imports: [
    NgClass,
    ButtonModule,
    DialogModule,
    TabViewModule,
    SharedModule,
    OnuEditFormComponent,
    OnuGraphsComponent,
    OnuNotesComponent,
    OnuStatusPanelComponent,
    OnuSyncFormComponent,
    OnuSplitterDialogComponent,
    OnuTr069Component,
  ],
  templateUrl: './datails-smart-olt-configurations.component.html',
})
export class DatailsSmartOltConfigurationsComponent implements OnInit {
  public view: IOnuView | null = null;
  public loading = true;
  public loadError = '';

  public diagKind: DiagKind | null = null;
  public diagOutput = '';
  public diagLoading = false;
  /** panel TR-069 en lugar de la consola de la OLT */
  public tr069Open = false;
  public readonly diagTitles = DIAG_TITLES;

  public history: IOnuHistoryItem[] | null = null;
  public activeTab = 0;
  public syncOpen = false;
  public splitterOpen = false;
  /** nombre del splitter abierto: se mantiene aunque la ONU salga de él */
  public splitterName = '';

  /** indicadores del mini dashboard */
  public kpis: {
    trafficDown: number | null;
    trafficUp: number | null;
    usageToday: { download: string; upload: string; total: string } | null;
    loaded: boolean;
  } = { trafficDown: null, trafficUp: null, usageToday: null, loaded: false };

  public wifi: IOnuViewWifi | null = null;
  public wifiLoading = false;
  public portLinks: Record<string, IOnuPortLink | null | undefined> = {};

  public editForm: OnuEditForm | null = null;
  public editPort: IOnuViewEthernetPort | null = null;
  public editWifiPort: IOnuViewWifiPort | null = null;
  public readonly editTitles = ONU_EDIT_TITLES;

  public actionLoading: Partial<Record<string, boolean>> = {};
  public deleteLoading = false;
  public readonly deviceActions = DEVICE_ACTIONS;

  constructor(
    public readonly dynamicDialogRef: DynamicDialogRef,
    public readonly dynamicDialogConfig: DynamicDialogConfig<{
      onuSignals?: any;
      onuStatus?: any;
      onuDetails?: IOnuDetails;
      contractID: number;
      sn?: string;
      /** id interno de SmartOLT cuando se abre desde otra pantalla (p. ej. el splitter) */
      onuId?: number;
      companyOwner: number;
    } | null>,
    private readonly oltService: OltService,
    private readonly onuViewService: OnuViewService,
    public readonly dialogService: DialogService,
    private readonly confirmationService: ConfirmationService,
    private readonly toastService: ToastrService,
  ) {}

  ngOnInit(): void {
    this.init();
  }

  private get data() {
    return this.dynamicDialogConfig.data;
  }

  private async init() {
    if (!this.data?.sn && !this.data?.onuId) {
      this.loading = false;
      return;
    }
    this.loading = true;
    this.loadError = '';
    try {
      const onuId =
        this.data.onuId ?? (await this.onuViewService.resolveOnuId(this.data.sn!)).data.onuId;
      await this.loadView(onuId);
      // el dashboard se completa en segundo plano, sin demorar la apertura
      this.loadKpis();
      this.refreshPortLinks();
    } catch (error) {
      this.loadError = 'No se pudo cargar la ONU desde SmartOLT.';
    } finally {
      this.loading = false;
    }
  }

  /**
   * loadView: vuelve a leer la pantalla completa (después de cada cambio)
   */
  public async loadView(onuId = this.view?.onuId) {
    if (!onuId) return;
    const response = await this.onuViewService.getView(onuId);
    this.view = response.data;
  }

  /**
   * diag: Get status / Show running-config / SW info, mostrados como en SmartOLT
   */
  public async diag(kind: DiagKind) {
    if (!this.view) return;
    this.tr069Open = false;
    this.diagKind = kind;
    this.diagLoading = true;
    this.diagOutput = '';
    try {
      const response = await this.onuViewService.read<{ response: string }>(
        this.view.onuId,
        kind as OnuReadResource,
      );
      this.diagOutput = this.toPlainText(response.data?.response ?? '');
    } catch (error) {
      this.diagOutput = 'No se pudo obtener la información de la OLT.';
    } finally {
      this.diagLoading = false;
    }
  }

  /**
   * openTr069: el botón TR-069 de SmartOLT; sin perfil TR-069 solo explica cómo activarlo
   */
  public openTr069() {
    this.diagKind = null;
    this.tr069Open = true;
  }

  /** La ONU tiene un perfil TR-069 (SmartOLT lo marca en el enlace de Mgmt IP). */
  public get tr069Enabled(): boolean {
    return this.view?.mgmtIp?.['tr069Enabled'] === '1';
  }

  /** La OLT responde texto con <span>/<br> y saltos \r; se muestra como texto plano. */
  private toPlainText(text: string): string {
    const html = text.replace(/<br\s*\/?>/gi, '\n').replace(/\r\n?/g, '\n');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent ?? '').trim();
  }

  /**
   * live: tráfico y estado en vivo (modal existente)
   */
  public async live() {
    const sn = this.data?.sn ?? this.view?.sn;
    if (!this.data || !sn) return;
    await this.oltService.showLiveStatus(sn, this.data.companyOwner);
  }

  /**
   * onTabChange: carga el historial al abrir su pestaña
   */
  public async onTabChange(index: number) {
    if (index === 1) {
      this.loadServiceExtras();
    }
    if (index === 4 && this.history === null && this.view) {
      try {
        const response = await this.onuViewService.read<IOnuHistoryItem[]>(
          this.view.onuId,
          'history',
        );
        this.history = response.data;
      } catch (error) {
        this.history = [];
      }
    }
  }

  /**
   * loadKpis: tráfico de la última lectura y consumo de hoy
   */
  public async loadKpis() {
    if (!this.view) return;
    const onuId = this.view.onuId;
    const [traffic, usage] = await Promise.allSettled([
      this.onuViewService.read<{ series: { name: string; points: [number, number | null][] }[] }>(
        onuId,
        'traffic',
        { period: 'hourly' },
      ),
      this.onuViewService.read<
        { key?: string; period: string; download: string; upload: string; total: string }[]
      >(onuId, 'traffic_usage'),
    ]);
    const lastValue = (name: RegExp) => {
      if (traffic.status !== 'fulfilled') return null;
      const points = traffic.value.data?.series?.find((s) => name.test(s.name))?.points ?? [];
      const values = points.map((p) => p[1]).filter((v): v is number => v !== null);
      return values.length ? values[values.length - 1] : null;
    };
    this.kpis = {
      trafficDown: lastValue(/download/i),
      trafficUp: lastValue(/upload/i),
      usageToday:
        usage.status === 'fulfilled'
          ? // key la pone el backend en cualquier idioma; el texto queda para respuestas antiguas
            usage.value.data?.find(
              (row) => row.key === 'today' || /^(today|hoy)$/i.test(row.period),
            ) ?? null
          : null,
      loaded: true,
    };
  }

  /** "Disconnections: 1 (24h) · 10 (7d)" → { day: 1, week: 10 } */
  public get disconnections(): { day: number; week: number } | null {
    const match = this.view?.disconnections.match(/(\d+)\s*\(24h\)\D+(\d+)\s*\(7d\)/);
    return match ? { day: Number(match[1]), week: Number(match[2]) } : null;
  }

  /** Puertos Ethernet con enlace, según la última lectura de la OLT. */
  public get linkedPorts(): { up: number; total: number; read: boolean } {
    const links = Object.values(this.portLinks);
    return {
      up: links.filter((link) => link?.link === 'up').length,
      total: this.view?.ethernetPorts.length ?? 0,
      read: links.length > 0 && links.every((link) => link !== undefined),
    };
  }

  public formatMbps(bps: number | null): string {
    return bps === null ? '–' : `${(bps / 1e6).toFixed(2)} Mbps`;
  }

  /** La insignia de desconexiones lleva al historial de estado, como en SmartOLT. */
  public openGraphsTab() {
    this.activeTab = 3;
  }

  /**
   * loadServiceExtras: WiFi y estado de enlace de los puertos, que SmartOLT carga aparte
   */
  public loadServiceExtras(force = false) {
    if (!this.view) return;
    if (this.view.hasWifi && (force || !this.wifi)) {
      this.loadWifi();
    }
    if (force || !Object.keys(this.portLinks).length) {
      this.refreshPortLinks();
    }
  }

  public async loadWifi() {
    if (!this.view) return;
    this.wifiLoading = true;
    try {
      const response = await this.onuViewService.read<IOnuViewWifi>(this.view.onuId, 'wifi');
      this.wifi = response.data;
    } catch (error) {
      this.wifi = null;
    } finally {
      this.wifiLoading = false;
    }
  }

  /**
   * refreshPortLinks: enlace (up/down, velocidad, dúplex) de cada puerto Ethernet
   */
  public refreshPortLinks() {
    if (!this.view) return;
    const onuId = this.view.onuId;
    this.view.ethernetPorts.forEach(async (port) => {
      const number = port.port.split('/').pop();
      if (!number) return;
      this.portLinks[port.id] = undefined;
      try {
        const response = await this.onuViewService.read<{ response: IOnuPortLink }>(
          onuId,
          'port_link',
          {
            port: number,
          },
        );
        this.portLinks[port.id] = response.data?.response?.supported
          ? response.data.response
          : null;
      } catch (error) {
        this.portLinks[port.id] = null;
      }
    });
  }

  public portLinkLabel(link: IOnuPortLink): string {
    if (link.link !== 'up') return 'Sin enlace';
    const speed = link.speed_mbps ? `${link.speed_mbps} Mbps` : '';
    return ['Enlace', speed, link.duplex ?? ''].filter(Boolean).join(' · ');
  }

  /**
   * toggleWifi: activa o desactiva el WiFi de la ONU (desactivar borra su configuración)
   */
  public toggleWifi(enable: boolean) {
    const run = async () => {
      if (!this.view) return;
      this.actionLoading['wifi_status'] = true;
      try {
        await this.onuViewService.action(
          this.view.onuId,
          'wifi_status',
          enable ? { wifi: '1' } : {},
        );
        this.toastService.success(enable ? 'WiFi habilitado' : 'WiFi deshabilitado');
        await this.loadWifi();
      } catch {
        // el interceptor HTTP ya mostró el error de SmartOLT
      } finally {
        this.actionLoading['wifi_status'] = false;
      }
    };
    if (enable) {
      run();
      return;
    }
    this.confirmationService.confirm({
      header: 'Deshabilitar WiFi',
      message:
        'Se deshabilitará el servicio inalámbrico de esta ONU y se perderá su configuración. ¿Continuar?',
      ...CONFIRM_DEFAULTS,
      acceptLabel: 'Sí',
      acceptButtonStyleClass: DANGER_BUTTON,
      accept: run,
    });
  }

  /**
   * edit: abre el formulario de edición correspondiente
   */
  public edit(
    form: OnuEditForm,
    port: IOnuViewEthernetPort | null = null,
    wifiPort: IOnuViewWifiPort | null = null,
  ) {
    this.editPort = port;
    this.editWifiPort = wifiPort;
    this.editForm = form;
  }

  /** Después de copiar datos del sistema a SmartOLT se vuelve a leer la pantalla. */
  public async onSynced(message: string) {
    this.syncOpen = false;
    this.toastService.success(message || 'Datos sincronizados con SmartOLT');
    this.history = null;
    await this.loadView();
  }

  /** Eliminar solo existe cuando la ONU se abrió desde un contrato del sistema. */
  public get canDelete(): boolean {
    return !!this.data?.contractID && !!this.data?.sn;
  }

  /** Detalle de otra ONU del splitter, en su propio modal. */
  public openSplitter() {
    if (!this.view) return;
    this.splitterName = this.view.location.odbName;
    this.splitterOpen = true;
  }

  public openSplitterOnu(onuId: string) {
    if (!this.data) return;
    this.dialogService.open(DatailsSmartOltConfigurationsComponent, {
      header: `ONU ${onuId}`,
      data: { onuId: Number(onuId), companyOwner: this.data.companyOwner },
      // PrimeNG no abre dos diálogos del mismo componente salvo que se permita
      duplicate: true,
      maximizable: true,
      width: ONU_DIALOG_WIDTH,
      height: ONU_DIALOG_HEIGHT,
      contentStyle: { 'padding-bottom': '0' },
      breakpoints: ONU_DIALOG_BREAKPOINTS,
    });
  }

  /** Después de cambiar algo en el splitter se vuelve a leer la ONU (su splitter y puerto). */
  public async onSplitterChanged() {
    await this.loadView();
  }

  public async onSaved(message: string) {
    const form = this.editForm;
    this.editForm = null;
    this.toastService.success(message || 'Cambios aplicados en SmartOLT');
    this.history = null;
    await this.loadView();
    if (form === 'wifi') {
      await this.loadWifi();
    }
  }

  /**
   * deviceAction: reboot, resync, restaurar, habilitar o deshabilitar, con confirmación
   */
  public deviceAction(action: keyof typeof DEVICE_ACTIONS) {
    const def = DEVICE_ACTIONS[action];
    this.confirmationService.confirm({
      header: def.label,
      message: def.message,
      ...CONFIRM_DEFAULTS,
      acceptLabel: 'Sí',
      acceptButtonStyleClass: def.severity === 'danger' ? DANGER_BUTTON : 'p-button-warning',
      accept: () => this.runAction(action as OnuAction),
    });
  }

  private async runAction(action: OnuAction) {
    if (!this.view) return;
    this.actionLoading[action] = true;
    try {
      const response = await this.onuViewService.action(this.view.onuId, action);
      this.toastService.success(response.message || 'Acción aplicada');
      this.history = null;
      await this.loadView();
    } catch {
      // el interceptor HTTP ya mostró el error de SmartOLT
    } finally {
      this.actionLoading[action] = false;
    }
  }

  /**
   * deleteOnu: elimina la ONU y la desvincula del contrato (flujo existente)
   */
  public deleteOnu() {
    if (!this.data) return;
    const data = this.data;
    this.confirmationService.confirm({
      header: 'Eliminar ONU',
      message: `Se eliminará la ONU <strong>${data.sn}</strong> de SmartOLT y del contrato. ¿Continuar?`,
      ...CONFIRM_DEFAULTS,
      acceptLabel: 'Sí, eliminar',
      acceptButtonStyleClass: DANGER_BUTTON,
      accept: async () => {
        this.deleteLoading = true;
        try {
          await this.oltService.deleteOnu({
            idContract: data.contractID,
            externalId: data.sn!,
            companyOwner: data.companyOwner,
          });
          this.dynamicDialogRef.close({ delete: true });
        } catch {
          // el interceptor HTTP ya mostró el error; el modal queda abierto
        } finally {
          this.deleteLoading = false;
        }
      },
    });
  }
}
